import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import WhatsAppWidget from '../../components/ui/WhatsAppWidget';
import NewsletterSubscription from '../../components/ui/NewsletterSubscription';
import ConsultationBooking from '../../components/ui/ConsultationBooking';
import ServiceCard from './components/ServiceCard';
import ProcessVisualization from './components/ProcessVisualization';
import FeaturedProjectsCarousel from './components/FeaturedProjectsCarousel';
import ServiceComparisonMatrix from './components/ServiceComparisonMatrix';
import TechnologyStack from './components/TechnologyStack';
import Icon from '../../components/AppIcon';

const ServicesPortfolio = () => {
  const [showConsultation, setShowConsultation] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
  {
    title: "Web & Mobile Development",
    description: "Custom web and mobile applications built with cutting-edge technologies, delivering seamless user experiences across all platforms and devices with scalable architecture.",
    image: "https://images.unsplash.com/photo-1542546068979-b6affb46ea8f",
    imageAlt: "Modern workspace with multiple computer monitors displaying colorful code and web development interfaces in dark theme",
    icon: "Code",
    deliveryTime: "4-12 weeks",
    startingPrice: "₦500,000",
    clientsServed: "150+",
    features: [
    "Responsive design for all devices",
    "Progressive Web App (PWA) capabilities",
    "API integration and development",
    "Performance optimization",
    "Cross-browser compatibility",
    "Ongoing maintenance and support"]

  },
  {
    title: "IT Consulting & Strategy",
    description: "Strategic IT consulting services helping businesses leverage technology for digital transformation, operational efficiency, and competitive advantage in the modern marketplace.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12ca8f8f2-1766749339258.png",
    imageAlt: "Business professionals in modern office having strategic meeting with laptops and documents discussing IT solutions",
    icon: "Briefcase",
    deliveryTime: "2-8 weeks",
    startingPrice: "₦300,000",
    clientsServed: "80+",
    features: [
    "Technology assessment and audit",
    "Digital transformation roadmap",
    "Infrastructure optimization",
    "Security and compliance review",
    "Vendor selection guidance",
    "Implementation support"]

  },
  {
    title: "Business Branding & Identity",
    description: "Comprehensive branding solutions creating memorable visual identities that resonate with target audiences and establish strong market presence for businesses of all sizes.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1654ebf47-1764773611909.png",
    imageAlt: "Creative designer workspace with brand identity materials including color palettes, typography samples, and logo sketches on desk",
    icon: "Palette",
    deliveryTime: "3-6 weeks",
    startingPrice: "₦250,000",
    clientsServed: "120+",
    features: [
    "Logo design and brand guidelines",
    "Visual identity system",
    "Marketing collateral design",
    "Brand strategy development",
    "Social media branding",
    "Brand refresh and evolution"]

  },
  {
    title: "Mentorship & Training Programs",
    description: "Personalized mentorship and comprehensive training programs empowering individuals and teams with technical skills, entrepreneurial mindset, and career development strategies.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d2667799-1767681169382.png",
    imageAlt: "Professional mentor teaching young professionals in modern training room with presentation screen and collaborative workspace",
    icon: "Users",
    deliveryTime: "Ongoing",
    startingPrice: "₦150,000",
    clientsServed: "200+",
    features: [
    "One-on-one mentorship sessions",
    "Group training workshops",
    "Career development planning",
    "Technical skills bootcamps",
    "Entrepreneurship guidance",
    "Continuous learning support"]

  }];


  const processes = [
  {
    title: "Discovery & Planning",
    description: "Understanding your vision and requirements",
    detailedDescription: "We begin with comprehensive discovery sessions to understand your business goals, target audience, technical requirements, and success metrics. This phase establishes the foundation for project success.",
    icon: "Search",
    duration: "1-2 weeks",
    activities: [
    "Initial consultation and requirement gathering",
    "Market research and competitor analysis",
    "Technical feasibility assessment",
    "Project scope definition and timeline creation",
    "Resource allocation and team formation"]

  },
  {
    title: "Design & Prototyping",
    description: "Creating visual concepts and user experiences",
    detailedDescription: "Our design team creates intuitive interfaces and engaging user experiences through wireframes, mockups, and interactive prototypes that align with your brand identity and user needs.",
    icon: "Palette",
    duration: "2-3 weeks",
    activities: [
    "User experience (UX) research and mapping",
    "Wireframe and mockup creation",
    "Interactive prototype development",
    "Design system establishment",
    "Client feedback and iteration cycles"]

  },
  {
    title: "Development & Integration",
    description: "Building robust and scalable solutions",
    detailedDescription: "Our development team brings designs to life using modern technologies and best practices, ensuring code quality, performance optimization, and seamless third-party integrations.",
    icon: "Code",
    duration: "4-8 weeks",
    activities: [
    "Frontend and backend development",
    "Database design and implementation",
    "API development and integration",
    "Security implementation",
    "Performance optimization and testing"]

  },
  {
    title: "Testing & Quality Assurance",
    description: "Ensuring flawless functionality and performance",
    detailedDescription: "Rigorous testing across multiple devices, browsers, and scenarios to identify and resolve issues before launch, ensuring a smooth user experience and reliable performance.",
    icon: "CheckCircle",
    duration: "1-2 weeks",
    activities: [
    "Functional and integration testing",
    "Cross-browser and device testing",
    "Performance and load testing",
    "Security vulnerability assessment",
    "User acceptance testing (UAT)"]

  },
  {
    title: "Deployment & Launch",
    description: "Going live with confidence",
    detailedDescription: "Smooth deployment to production environments with comprehensive monitoring, ensuring zero downtime and immediate issue resolution during the critical launch phase.",
    icon: "Rocket",
    duration: "3-5 days",
    activities: [
    "Production environment setup",
    "Data migration and configuration",
    "Deployment automation",
    "Launch monitoring and support",
    "Performance tracking and optimization"]

  },
  {
    title: "Support & Maintenance",
    description: "Ongoing optimization and support",
    detailedDescription: "Continuous monitoring, updates, and enhancements to ensure your solution remains secure, performant, and aligned with evolving business needs and technology standards.",
    icon: "Settings",
    duration: "Ongoing",
    activities: [
    "24/7 monitoring and support",
    "Regular updates and patches",
    "Performance optimization",
    "Feature enhancements",
    "Technical documentation updates"]

  }];


  const featuredProjects = [
  {
    title: "E-Commerce Platform Transformation",
    description: "Complete digital transformation of a traditional retail business into a modern e-commerce platform with mobile app, resulting in 300% increase in online sales and expanded market reach across West Africa.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_135a5c963-1764669618326.png",
    imageAlt: "Modern e-commerce website displayed on multiple devices showing product catalog, shopping cart, and checkout process",
    industry: "Retail & E-Commerce",
    duration: "6 months",
    tags: ["React", "Node.js", "MongoDB", "AWS"],
    results: [
    "300% increase in online sales within first quarter",
    "50,000+ registered users in 6 months",
    "40% reduction in operational costs",
    "Expanded to 3 new countries"],

    testimonial: "Edrian transformed our business completely. The platform is intuitive, fast, and has opened up new markets we never thought possible.",
    clientName: "Adebayo Okonkwo",
    clientRole: "CEO, AfriMart Retail"
  },
  {
    title: "FinTech Mobile Banking Solution",
    description: "Development of secure mobile banking application with biometric authentication, real-time transactions, and AI-powered financial insights, serving over 100,000 active users across Nigeria and Cameroon.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1eff135a3-1766601351747.png",
    imageAlt: "Smartphone displaying modern mobile banking app interface with transaction history, account balance, and financial analytics",
    industry: "Financial Technology",
    duration: "8 months",
    tags: ["React Native", "Python", "PostgreSQL", "Blockchain"],
    results: [
    "100,000+ active users in first year",
    "99.9% uptime and reliability",
    "Zero security breaches",
    "4.8/5 app store rating"],

    testimonial: "The security and user experience of this app are exceptional. Our customers trust us with their finances, and this platform delivers on that trust.",
    clientName: "Chioma Nwankwo",
    clientRole: "CTO, PayFlow Africa"
  },
  {
    title: "Healthcare Management System",
    description: "Comprehensive hospital management system streamlining patient records, appointment scheduling, billing, and telemedicine capabilities, improving healthcare delivery efficiency by 60% across 15 medical facilities.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a07fec34-1764641716124.png",
    imageAlt: "Healthcare professional using tablet displaying patient management system with medical records and appointment scheduling interface",
    industry: "Healthcare & Medical",
    duration: "10 months",
    tags: ["Angular", "Java", "MySQL", "HIPAA Compliant"],
    results: [
    "60% improvement in operational efficiency",
    "15 medical facilities integrated",
    "30,000+ patient records digitized",
    "Telemedicine adoption by 80% of doctors"],

    testimonial: "This system has revolutionized how we manage patient care. The efficiency gains are remarkable, and our staff loves the intuitive interface.",
    clientName: "Dr. Kwame Mensah",
    clientRole: "Medical Director, HealthCare Plus"
  }];


  const pricingTiers = [
  {
    name: "Starter",
    description: "Perfect for small businesses and startups",
    icon: "Zap",
    price: {
      NGN: 250000,
      XAF: 250000,
      ZAR: 15000
    },
    billingPeriod: "project",
    popular: false,
    features: [
    { text: "Basic website or mobile app", included: true },
    { text: "Up to 5 pages/screens", included: true },
    { text: "Responsive design", included: true },
    { text: "Basic SEO optimization", included: true },
    { text: "1 month support", included: true },
    { text: "Advanced analytics", included: false },
    { text: "Custom integrations", included: false },
    { text: "Priority support", included: false }],

    ctaText: "Get Started"
  },
  {
    name: "Professional",
    description: "Ideal for growing businesses",
    icon: "TrendingUp",
    price: {
      NGN: 750000,
      XAF: 750000,
      ZAR: 45000
    },
    billingPeriod: "project",
    popular: true,
    features: [
    { text: "Advanced web/mobile application", included: true },
    { text: "Up to 15 pages/screens", included: true },
    { text: "Custom design system", included: true },
    { text: "Advanced SEO & performance", included: true },
    { text: "3 months support", included: true },
    { text: "Advanced analytics integration", included: true },
    { text: "API development", included: true },
    { text: "Priority support", included: false }],

    ctaText: "Choose Professional"
  },
  {
    name: "Enterprise",
    description: "For large-scale operations",
    icon: "Building",
    price: {
      NGN: 2000000,
      XAF: 2000000,
      ZAR: 120000
    },
    billingPeriod: "project",
    popular: false,
    features: [
    { text: "Enterprise-grade solution", included: true },
    { text: "Unlimited pages/screens", included: true },
    { text: "Custom architecture design", included: true },
    { text: "Enterprise SEO strategy", included: true },
    { text: "12 months support", included: true },
    { text: "Advanced analytics & BI", included: true },
    { text: "Multiple API integrations", included: true },
    { text: "24/7 priority support", included: true }],

    ctaText: "Contact Sales"
  }];


  const technologies = [
  {
    category: "Frontend Development",
    icon: "Monitor",
    items: [
    {
      name: "Vue.js",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_181a557ae-1764675852340.png",
      logoAlt: "Vue.js progressive framework logo with green V symbol",
      proficiency: 4
    },
    {
      name: "Angular",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_14283cbd9-1764661435128.png",
      logoAlt: "Angular framework logo with red shield and A letter",
      proficiency: 4
    },
    {
      name: "TypeScript",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c828b0a4-1764655537527.png",
      logoAlt: "TypeScript programming language logo with blue TS letters",
      proficiency: 5
    },
    {
      name: "Tailwind CSS",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1cbbb7836-1764655536289.png",
      logoAlt: "Tailwind CSS utility-first framework logo with teal wave symbol",
      proficiency: 5
    }]

  },
  {
    category: "Backend Development",
    icon: "Server",
    items: [
    {
      name: "Node.js",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1801868b5-1764675849904.png",
      logoAlt: "Node.js JavaScript runtime logo with green hexagon",
      proficiency: 5
    },
    {
      name: "Python",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1d8a74e23-1764641706225.png",
      logoAlt: "Python programming language logo with blue and yellow snake symbol",
      proficiency: 4
    },
    {
      name: "Java",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_101c48a12-1766552741400.png",
      logoAlt: "Java programming language logo with red coffee cup",
      proficiency: 4
    },
    {
      name: "PostgreSQL",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_104143a7c-1764641705124.png",
      logoAlt: "PostgreSQL database logo with blue elephant symbol",
      proficiency: 5
    },
    {
      name: "MongoDB",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_14f5c244f-1766597613017.png",
      logoAlt: "MongoDB NoSQL database logo with green leaf symbol",
      proficiency: 5
    }]

  },
  {
    category: "Mobile Development",
    icon: "Smartphone",
    items: [
    {
      name: "Flutter",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1fbbc0507-1764686686772.png",
      logoAlt: "Flutter UI framework logo with blue F letter",
      proficiency: 4
    },
    {
      name: "Swift",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_147e740eb-1764722055496.png",
      logoAlt: "Swift programming language logo with orange bird symbol",
      proficiency: 3
    },
    {
      name: "Kotlin",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_137721600-1764722052982.png",
      logoAlt: "Kotlin programming language logo with purple K letter",
      proficiency: 3
    }]

  },
  {
    category: "Cloud & DevOps",
    icon: "Cloud",
    items: [
    {
      name: "AWS",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1815fd195-1764675850118.png",
      logoAlt: "Amazon Web Services cloud platform logo with orange smile arrow",
      proficiency: 5
    },
    {
      name: "Docker",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1f9b7b4eb-1764655536509.png",
      logoAlt: "Docker containerization platform logo with blue whale",
      proficiency: 5
    },
    {
      name: "Kubernetes",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_15a154113-1764675852012.png",
      logoAlt: "Kubernetes container orchestration logo with blue ship wheel",
      proficiency: 4
    },
    {
      name: "Git",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1594437d4-1764641704037.png",
      logoAlt: "Git version control system logo with orange branching symbol",
      proficiency: 5
    }]

  }];


  const handleBookConsultation = (service) => {
    setSelectedService(service);
    setShowConsultation(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Services Portfolio - Edrian Bass | IT Engineering & Business Solutions</title>
        <meta
          name="description"
          content="Explore comprehensive IT services including web/mobile development, IT consulting, business branding, and mentorship programs. Transform your business with cutting-edge technology solutions." />

      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <WhatsAppWidget />

        <main className="pt-20 md:pt-24 lg:pt-28">
          <section className="px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12 md:mb-16 lg:mb-20">

                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4 md:mb-6">
                  <Icon name="Briefcase" size={20} color="var(--color-primary)" />
                  <span className="text-sm md:text-base font-medium text-primary">Professional Services</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground mb-4 md:mb-6">
                  Transform Your Business with
                  <span className="block text-primary mt-2">Expert IT Solutions</span>
                </h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
                  Comprehensive technology services designed to drive innovation, efficiency, and growth for businesses across Africa
                </p>
              </motion.div>

              {showConsultation &&
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mb-12 md:mb-16 lg:mb-20">

                  <div className="max-w-2xl mx-auto">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                        Book Your Consultation
                      </h2>
                      <button
                      onClick={() => setShowConsultation(false)}
                      className="w-8 h-8 rounded-full bg-muted hover:bg-destructive transition-colors flex items-center justify-center"
                      aria-label="Close consultation booking">

                        <Icon name="X" size={16} color="currentColor" />
                      </button>
                    </div>
                    <ConsultationBooking />
                  </div>
                </motion.div>
              }

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-10 mb-16 md:mb-20 lg:mb-24">
                {services?.map((service, index) =>
                <ServiceCard
                  key={index}
                  service={service}
                  index={index}
                  onBookConsultation={handleBookConsultation} />

                )}
              </div>

              <div className="mb-16 md:mb-20 lg:mb-24">
                <ProcessVisualization processes={processes} />
              </div>

              <div className="mb-16 md:mb-20 lg:mb-24">
                <FeaturedProjectsCarousel projects={featuredProjects} />
              </div>

              <div className="mb-16 md:mb-20 lg:mb-24">
                <ServiceComparisonMatrix pricingTiers={pricingTiers} />
              </div>

              <div className="mb-16 md:mb-20 lg:mb-24">
                <TechnologyStack technologies={technologies} />
              </div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-10">

                <NewsletterSubscription />
                <div className="bg-card rounded-xl p-6 md:p-8 border border-border">
                  <div className="flex items-center gap-3 mb-4 md:mb-6">
                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="MessageCircle" size={24} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-semibold text-foreground">Ready to Start?</h3>
                      <p className="text-sm md:text-base text-muted-foreground">Let's discuss your project</p>
                    </div>
                  </div>
                  <p className="text-sm md:text-base text-muted-foreground mb-6">
                    Have a project in mind? Get in touch and let's create something amazing together. Free consultation for all new clients.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm md:text-base">
                      <Icon name="Mail" size={18} color="var(--color-primary)" />
                      <span className="text-muted-foreground">edrianbass@business.com</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm md:text-base">
                      <Icon name="Phone" size={18} color="var(--color-success)" />
                      <span className="text-muted-foreground">+234 801 234 5678</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm md:text-base">
                      <Icon name="MapPin" size={18} color="var(--color-accent)" />
                      <span className="text-muted-foreground">Lagos, Nigeria</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        <footer className="bg-card border-t border-border py-6 md:py-8">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm md:text-base text-muted-foreground text-center md:text-left">
                © {new Date()?.getFullYear()} Edrian Bass. All rights reserved.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-muted hover:bg-primary transition-colors flex items-center justify-center"
                  aria-label="LinkedIn profile">

                  <Icon name="Linkedin" size={18} color="currentColor" />
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-muted hover:bg-primary transition-colors flex items-center justify-center"
                  aria-label="Twitter profile">

                  <Icon name="Twitter" size={18} color="currentColor" />
                </a>
                <a
                  href="https://github.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-muted hover:bg-primary transition-colors flex items-center justify-center"
                  aria-label="GitHub profile">

                  <Icon name="Github" size={18} color="currentColor" />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>);





};

export default ServicesPortfolio;